import type { DateTime } from 'effect';

import type { LocalDate } from '@js-joda/core';
import { DateSerializable } from '@vs-shop/schemas/DateSerializable';
import { getCurrentLocalDate } from '../../../shared/utils/jodaLocalDate';
import { Data, Effect, Option } from 'effect';

import { DISPATCH_DATE_IN_A_FEW_DAYS } from '../../../config/dispatchDate';

import { decodeLocalDateFromDateSerializable } from '@vs-shop/schemas/datetime/LocalDateFromDateSerializable';

/**
 * Parses a SerializableDate to LocalDate within an Effect context.
 * Returns Option.none() if date is null or parsing fails.
 */
export const parseDispatchDate = (
  date: DateSerializable | null
): Effect.Effect<Option.Option<LocalDate>> =>
  date === null
    ? Effect.succeed(Option.none())
    : decodeLocalDateFromDateSerializable(date).pipe(Effect.option);

/**
 * Checks if a dispatch date is delayed (more than DISPATCH_DATE_IN_A_FEW_DAYS from today).
 */
export const isDispatchDelayed = (
  localDate: Option.Option<LocalDate>,
  today: LocalDate,
  hasStock: boolean
): boolean =>
  hasStock &&
  Option.isSome(localDate) &&
  localDate.value.isAfter(today.plusDays(DISPATCH_DATE_IN_A_FEW_DAYS));

// -----------------------------------------------------------------------------
// Tagged Union for Dispatch Date Result
// -----------------------------------------------------------------------------

/**
 * Tagged union representing dispatch date comparison result.
 * Use with Match.tag for exhaustive pattern matching.
 *
 * Note: `Later` contains a SerializableDate rather than LocalDate,
 * making it safe to pass across Server/Client Component boundaries.
 */
/* eslint-disable @typescript-eslint/no-empty-object-type */
export type DispatchDateResult = Data.TaggedEnum<{
  Today: {};
  Tomorrow: {};
  DayAfterTomorrow: {};
  Later: { readonly date: DateSerializable };
  NoDate: {};
}>;
/* eslint-enable @typescript-eslint/no-empty-object-type */

export const DispatchDateResult = Data.taggedEnum<DispatchDateResult>();

/**
 * Compares a dispatch date against today and returns a tagged result.
 * Use Match.tag for exhaustive pattern matching in components.
 *
 * @example
 * ```ts
 * const result = compareDispatchDateTagged(localDate, today);
 * Match.value(result).pipe(
 *   Match.tag('Today', () => 'dnes'),
 *   Match.tag('Tomorrow', () => 'zítra'),
 *   Match.tag('DayAfterTomorrow', () => 'pozítří'),
 *   Match.tag('Later', ({ date }) => formatDate(date.value)),
 *   Match.tag('NoDate', () => null),
 *   Match.exhaustive
 * )
 * ```
 */
export const compareDispatchDateTagged = (
  localDate: Option.Option<LocalDate>,
  today: LocalDate
): DispatchDateResult => {
  if (Option.isNone(localDate)) {
    return DispatchDateResult.NoDate();
  }

  const date = localDate.value;
  const tomorrow = today.plusDays(1);
  const dayAfterTomorrow = today.plusDays(2);

  if (date.equals(today)) {
    return DispatchDateResult.Today();
  }
  if (date.equals(tomorrow)) {
    return DispatchDateResult.Tomorrow();
  }
  if (date.equals(dayAfterTomorrow)) {
    return DispatchDateResult.DayAfterTomorrow();
  }

  // Return SerializableDate for safe passing across component boundaries
  return DispatchDateResult.Later({
    date: DateSerializable.make({
      _tag: 'Date',
      value: date.toString(),
    }),
  });
};

/**
 * Effect that computes dispatch date tagged result.
 * Combines parsing and comparison in a single Effect.
 */
export const computeDispatchDateResult = (
  date: DateSerializable | null,
  today: LocalDate
): Effect.Effect<DispatchDateResult> =>
  parseDispatchDate(date).pipe(
    Effect.map((localDate) => compareDispatchDateTagged(localDate, today))
  );

/**
 * Effect that checks if dispatch is delayed.
 * Combines parsing and delay check in a single Effect.
 */
export const computeIsDispatchDelayed = (
  date: DateSerializable | null,
  today: LocalDate,
  hasStock: boolean
): Effect.Effect<boolean> =>
  parseDispatchDate(date).pipe(
    Effect.map((localDate) => isDispatchDelayed(localDate, today, hasStock))
  );

// -----------------------------------------------------------------------------
// Effects with today included (for component convenience)
// -----------------------------------------------------------------------------

/**
 * Effect that computes dispatch date tagged result with today obtained from CurrentTimeZone.
 * Component doesn't need to obtain today separately.
 */
export const getDispatchDateResult = (
  date: DateSerializable | null
): Effect.Effect<DispatchDateResult, never, DateTime.CurrentTimeZone> =>
  getCurrentLocalDate.pipe(Effect.flatMap((today) => computeDispatchDateResult(date, today)));

/**
 * Effect that checks if dispatch is delayed with today obtained from CurrentTimeZone.
 * Component doesn't need to obtain today separately.
 */
export const getIsDispatchDelayed = (
  date: DateSerializable | null,
  hasStock: boolean
): Effect.Effect<boolean, never, DateTime.CurrentTimeZone> =>
  getCurrentLocalDate.pipe(
    Effect.flatMap((today) => computeIsDispatchDelayed(date, today, hasStock))
  );
